import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";
import MasterExcelToolbar from "../components/MasterExcelToolbar";
import React from "react";

const DEFECT_TYPE_OPTIONS = ["Dimensional", "Material", "Visual", "Surface Finish", "Process", "Other"];

export default function DefectMaster() {
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", defect_type: "" });
  const [search, setSearch] = useState("");

  const fetchDefects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/defect-master`);
      setDefects(res.data);
    } catch { toast.error("Failed to load defects"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDefects(); }, [fetchDefects]);

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error("Defect name is required"); return; }
    if (!form.defect_type) { toast.error("Defect type is required"); return; }
    try {
      await axios.post(`${API}/defect-master`, form);
      toast.success("Defect added");
      setForm({ name: "", defect_type: "" });
      setShowAddForm(false);
      fetchDefects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
  };

  const handleEdit = async () => {
    if (!form.name.trim() || !form.defect_type) { toast.error("All fields required"); return; }
    try {
      await axios.put(`${API}/defect-master/${editId}`, form);
      toast.success("Defect updated");
      setEditId(null);
      setForm({ name: "", defect_type: "" });
      fetchDefects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this defect?")) return;
    try {
      await axios.delete(`${API}/defect-master/${id}`);
      toast.success("Deleted");
      fetchDefects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const startEdit = (d) => {
    setEditId(d.id);
    setForm({ name: d.name, defect_type: d.defect_type });
    setShowAddForm(false);
  };

  const sq = search.toLowerCase().trim();
  const filtered = sq ? defects.filter(d =>
    (d.code || "").toLowerCase().includes(sq) ||
    (d.name || "").toLowerCase().includes(sq) ||
    (d.defect_type || "").toLowerCase().includes(sq)
  ) : defects;

  return (
    <Layout>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Defect Master</h1>
            <p className="text-sm text-[#64748B] mt-1">Manage defect codes, names and types</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => { setShowAddForm(true); setEditId(null); setForm({ name: "", defect_type: "" }); }}
              className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px]" data-testid="add-defect-btn">
              <Plus size={16} className="mr-1" /> Add Defect
            </Button>
            <MasterExcelToolbar basePath="/defect-master" entityLabel="Defects" onImportDone={fetchDefects} />
          </div>
        </div>

        {/* Add / Edit Form */}
        {(showAddForm || editId) && (
          <Card className="p-5 border border-[#E2E8F0]" data-testid="defect-form-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[#1D2951]" style={{ fontFamily: 'Chivo' }}>
                {editId ? "Edit Defect" : "Add New Defect"}
              </h3>
              <button onClick={() => { setShowAddForm(false); setEditId(null); setForm({ name: "", defect_type: "" }); }}
                className="text-[#64748B] hover:text-[#1D2951]">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold">Defect Code</Label>
                <Input value={editId ? defects.find(d => d.id === editId)?.code || "" : "Auto (D-XXX)"}
                  readOnly className="min-h-[44px] bg-gray-100 font-mono" data-testid="defect-code-display" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Defect Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Dimensional Issue" className="min-h-[44px]" data-testid="defect-name-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Defect Type *</Label>
                <Select value={form.defect_type} onValueChange={v => setForm(p => ({ ...p, defect_type: v }))}>
                  <SelectTrigger className="min-h-[44px]" data-testid="defect-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {DEFECT_TYPE_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Button onClick={editId ? handleEdit : handleAdd}
                className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px] px-8" data-testid="save-defect-btn">
                {editId ? "Update Defect" : "Add Defect"}
              </Button>
            </div>
          </Card>
        )}

        {/* Search */}
        <div className="flex items-center gap-3">
          <Input placeholder="Search by code, name, or type..." value={search} onChange={e => setSearch(e.target.value)}
            className="max-w-sm min-h-[40px]" data-testid="defect-search-input" />
          <span className="text-sm text-[#64748B]">{filtered.length} defects</span>
        </div>

        {/* Table */}
        <Card className="border border-[#E2E8F0]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="defect-master-table">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-bold w-[120px]">Defect Code</th>
                  <th className="px-4 py-3 text-left font-bold">Defect Name</th>
                  <th className="px-4 py-3 text-left font-bold w-[180px]">Defect Type</th>
                  <th className="px-4 py-3 text-center font-bold w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {loading ? (
                  <tr><td colSpan="4" className="text-center py-8 text-[#64748B]">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-8 text-[#64748B]">No defects found. Add your first defect above.</td></tr>
                ) : filtered.map((d, idx) => (
                  <tr key={d.id} className="hover:bg-[#F8FAFC]" data-testid={`defect-row-${idx}`}>
                    <td className="px-4 py-3 font-mono font-bold text-[#1D2951]">{d.code}</td>
                    <td className="px-4 py-3 font-semibold">{d.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        d.defect_type === "Dimensional" ? "bg-[#EEF2FF] text-[#4338CA]" :
                        d.defect_type === "Material" ? "bg-[#FEF2F2] text-[#DC2626]" :
                        d.defect_type === "Visual" ? "bg-[#FFFBEB] text-[#D97706]" :
                        d.defect_type === "Surface Finish" ? "bg-[#ECFDF5] text-[#059669]" :
                        "bg-[#F1F5F9] text-[#475569]"
                      }`}>{d.defect_type}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(d)}
                          className="h-8 w-8 p-0 text-[#3B82F6]" data-testid={`edit-defect-${idx}`}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(d.id)}
                          className="h-8 w-8 p-0 text-[#EF4444]" data-testid={`delete-defect-${idx}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
